import { ReactNode, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import Blade from '@/components/ui/Blade/Blade';
import BladeSimpleSection from '@/components/ui/BladeSimpleSection/BladeSimpleSection';
// TODO: Timeline imports commented out temporarily
// import { SupportRequestStatuses } from '@/models/common/SupportRequestStatuses';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import { SupportRequestAdditionalInfoDtoIn } from '@/models/in/SupportRequestAdditionalInfoDtoIn';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import AllowedFor from '@/models/common/AllowedFor';
import SupportRequestType from '@/models/common/SupportRequestType';
import { getStatusColor, getAggregatedStatus } from '@/helpers/requestStatus';
import { getDetailStatusDisplayId } from '@/helpers/requestDetailStatus';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { formatDateLocale } from '@/helpers/dateHelper';
import { getSupplierDisplayDetails } from '@/helpers/supplierDisplayHelper';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import useFundContext from '@/contexts/Fund/useFundContext';
import CompletionUploadSection from '@/components/CompletionUploadSection/CompletionUploadSection';
import ReturnedPaymentSection from '../ReturnedPaymentSection/ReturnedPaymentSection';
import useReturnedPayments from '../ReturnedPaymentSection/useReturnedPayments';
import { ReturnedPaymentDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import Button from '@/components/ui/Button';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import donation from '@/assets/icons/donation.svg';
// import requestSupport from '@/assets/icons/request-support.svg';
// import approvedSupport from '@/assets/icons/approved-support.svg';
// import approvedSupportInactive from '@/assets/icons/approved-support-inactive.svg';
// import requestExecution from '@/assets/icons/request-execution.svg';
// import requestExecutionInactive from '@/assets/icons/request-execution-inactive.svg';
// import timeline from '@/assets/icons/timeline.svg';
// import timelineInactive from '@/assets/icons/timeline-inactive.svg';
// import waitingApproval from '@/assets/icons/waiting-approval.svg';
// import waitingApprovalInactive from '@/assets/icons/waiting-approval-inactive.svg';
import documentsIcon from '@/assets/icons/documents.svg';
import bank from '@/assets/icons/bank.svg';
import infoIcon from '@/assets/icons/info2.svg';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import TextRowsSkeleton from '@/components/ui/Skeleton/TextRowsSkeleton';
import Skeleton from '@mui/material/Skeleton';
import styles from './AdditionalInfo.module.css';

interface AdditionalInfoProps {
  request: SupportRequestDtoIn;
  handleXclick: () => void;
  onSupportedNameClick: (supportedName: string) => void;
  onCompletionSuccess: () => void;
  /** HAT-1395: opens «עדכון חשבון בנק» for the deleted account this request pays to. */
  onUpdateBankAccount: (group: ReturnedPaymentDtoIn) => void;
}

const AdditionalInfo = ({ request, handleXclick, onSupportedNameClick, onCompletionSuccess, onUpdateBankAccount }: AdditionalInfoProps) => {
  const { t, i18n } = useTranslation();
  const { currentFund } = useFundContext();

  const isPendingCompletion = request.statuses.includes('PendingCompletion') || request.statuses.includes('DonePendingCompletion');
  const canUploadDocuments = request.isInInitialStatusProcess || (isPendingCompletion && request.type !== SupportRequestType.Monthly);

  const handleCompletionSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['supportRequests'] });
    queryClient.invalidateQueries({ queryKey: [`funds/${currentFund?.id}/support-requests/${request.correlationId}/additional-info`] });
    onCompletionSuccess();
  };

  const { data: additionalInfo, isLoading } = useGetAuthApi<SupportRequestAdditionalInfoDtoIn>(
    currentFund && request ? `funds/${currentFund.id}/support-requests/${request.correlationId}/additional-info` : ''
  );

  // const lastChangedDate = '2024-01-01T00:00:00Z'; // TODO: replace with value from support-requests additional info API (commented out - used only in timeline)
  const documents = additionalInfo?.documents ?? [];

  const isAmountNegative = request.amount < 0;
  const amountColors = isAmountNegative ? 'text-(--font-primary) bg-(--surface-primary-lowest)' : 'text-(--font-tertiary) bg-(--surface-tertiary-low)';

  // TODO: Timeline code commented out temporarily
  // const numberStatus = Number(SupportRequestStatuses[request.status]);

  // Fund-expense requests have no BeneficiaryContact - render the supplier (or payer, when
  // the fund reimburses a private payer) name in the header instead, otherwise the blade
  // would only show the goal name and the user would have no way to tell which vendor the
  // request is for.
  const isFundExpense = request.goal?.allowedFor === AllowedFor.Fund;
  const fundExpenseDetails = request.additionalDetails as FundExpenseDetails | undefined;
  const isAlreadyPaid = !!fundExpenseDetails?.isAlreadyPaid;
  const supplierDisplay = isFundExpense ? getSupplierDisplayDetails(request.supplier, request.additionalDetails) : null;
  const fundExpenseRecipientName = isFundExpense ? (isAlreadyPaid ? (fundExpenseDetails?.payerName ?? '') : (supplierDisplay?.name ?? '')) : '';

  const sections: ReactNode[] = [];
  const waitingForAccount = useReturnedPayments().groupOf(request.correlationId);

  // Render the bank-account section for beneficiary requests. Fund-expense requests get the
  // same bank info inside their dedicated supplier/payer sections below, so we skip this one
  // to avoid showing the account twice without context.
  if (additionalInfo?.paymentInfo && !isFundExpense) {
    sections.push(
      <BladeSimpleSection key="bank-details" title={t('bankTransferToAccount')}>
        {/* HAT-1395: while the request waits for another account, the deleted one is not where
            the money goes - so it is not shown as if it were. Its number is in the box and the
            window, where it helps to fix it. A paid request whose account was deleted later
            still shows it: that is where its money went. */}
        {waitingForAccount ? (
          <div className="flex flex-col gap-3 items-start pt-0.5 pb-0.5">
            <span className="body-lr-regular text-(--font-body-1)">{t('returnedPayment.noAccountYet')}</span>
            <Button variant="outline" onClick={() => onUpdateBankAccount(waitingForAccount)}>
              {t('returnedPayment.menuLabel')}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 items-start pt-0.5 pb-0.5">
            <img src={bank} alt="" className="p-0.5" />
            <div className="flex flex-col">
              <span className="body-lr-regular text-(--font-body-1)">{additionalInfo.paymentInfo.accountOwnerName}</span>
              <span className="body-lr-regular text-(--font-body-1)">
                {additionalInfo.paymentInfo.accountNumber}/{additionalInfo.paymentInfo.branchNumber}
              </span>
              <span className="body-lr-regular text-(--font-body-1)">
                {additionalInfo.paymentInfo.bankName} {additionalInfo.paymentInfo.bankNumber}
              </span>
            </div>
          </div>
        )}
      </BladeSimpleSection>
    );
  }

  // For fund-expense requests we surface the full picture so the user can answer:
  //   1. Was this paid already? (payment status)
  //   2. Who is the vendor? (supplier name + taxId — shown in both flows, since the supplier
  //      is the entity the money was/will be spent at, even when a private payer fronted it)
  //   3. Where does the money go? (supplier bank when the fund pays the vendor directly,
  //      payer bank when the fund reimburses an already-paid invoice)
  // Without this block the user could only see one of the two names (supplier OR payer) and
  // had no way to tell which scenario they were looking at.
  if (isFundExpense) {
    sections.push(
      <BladeSimpleSection key="fund-expense-status" title={t('fundExpenseDetails')}>
        <div className="flex gap-2">
          <span className="body-lr-regular text-(--font-body-2)">{t('paymentStatus')}:</span>
          <span className="body-lr-regular text-(--font-body-1)">{isAlreadyPaid ? t('alreadyPaid') : t('notYetPaid')}</span>
        </div>
      </BladeSimpleSection>
    );

    sections.push(
      <BladeSimpleSection key="supplier-details" title={t('supplierDetails')}>
        <div className="flex flex-col gap-1">
          {supplierDisplay?.name && (
            <div className="flex gap-2">
              <span className="body-lr-regular text-(--font-body-2)">{t('supplierName')}:</span>
              <span className="body-lr-regular text-(--font-body-1)">{supplierDisplay.name}</span>
            </div>
          )}
          {supplierDisplay?.taxId && (
            <div className="flex gap-2">
              <span className="body-lr-regular text-(--font-body-2)">{t('supplierTaxId')}:</span>
              <span className="body-lr-regular text-(--font-body-1)">{supplierDisplay.taxId}</span>
            </div>
          )}
          {request.invoiceNumber && (
            <div className="flex gap-2">
              <span className="body-lr-regular text-(--font-body-2)">{t('invoiceNumber')}:</span>
              <span className="body-lr-regular text-(--font-body-1)">{request.invoiceNumber}</span>
            </div>
          )}
          {/* Supplier bank account is only relevant when the fund pays the vendor directly.
              When isAlreadyPaid=true the supplier's bank lives on the SupplierPaymentInfo
              but no money flows there — we show the payer's bank in the payer section
              below instead. */}
          {!isAlreadyPaid && additionalInfo?.paymentInfo && (
            <div className="flex gap-2 items-start pt-1">
              <img src={bank} alt="" className="p-0.5" />
              <div className="flex flex-col">
                <span className="body-lr-regular text-(--font-body-1)">{additionalInfo.paymentInfo.accountOwnerName}</span>
                <span className="body-lr-regular text-(--font-body-1)">
                  {additionalInfo.paymentInfo.accountNumber}/{additionalInfo.paymentInfo.branchNumber}
                </span>
                <span className="body-lr-regular text-(--font-body-1)">
                  {additionalInfo.paymentInfo.bankName} {additionalInfo.paymentInfo.bankNumber}
                </span>
              </div>
            </div>
          )}
        </div>
      </BladeSimpleSection>
    );

    if (isAlreadyPaid) {
      sections.push(
        <BladeSimpleSection key="payer-details" title={t('payerDetails')}>
          <div className="flex flex-col gap-1">
            {fundExpenseDetails?.payerName && (
              <div className="flex gap-2">
                <span className="body-lr-regular text-(--font-body-2)">{t('payerName')}:</span>
                <span className="body-lr-regular text-(--font-body-1)">{fundExpenseDetails.payerName}</span>
              </div>
            )}
            {fundExpenseDetails?.payerIsraeliId && (
              <div className="flex gap-2">
                <span className="body-lr-regular text-(--font-body-2)">{t('payerIsraeliId')}:</span>
                <span className="body-lr-regular text-(--font-body-1)">{fundExpenseDetails.payerIsraeliId}</span>
              </div>
            )}
            {additionalInfo?.paymentInfo && (
              <div className="flex gap-2 items-start pt-1">
                <img src={bank} alt="" className="p-0.5" />
                <div className="flex flex-col">
                  <span className="body-lr-regular text-(--font-body-1)">{additionalInfo.paymentInfo.accountOwnerName}</span>
                  <span className="body-lr-regular text-(--font-body-1)">
                    {additionalInfo.paymentInfo.accountNumber}/{additionalInfo.paymentInfo.branchNumber}
                  </span>
                  <span className="body-lr-regular text-(--font-body-1)">
                    {additionalInfo.paymentInfo.bankName} {additionalInfo.paymentInfo.bankNumber}
                  </span>
                </div>
              </div>
            )}
          </div>
        </BladeSimpleSection>
      );
    }
  }

  const headerTop = (
    <span className="body-lr-regular">
      {t(request.goal?.name || '')}
      {request.supportedContact && (
        <>
          {' - '}
          <span className="body-lr-link" onClick={() => onSupportedNameClick(request.supportedContact!.firstName + ' ' + request.supportedContact!.lastName)}>
            {request.supportedContact.firstName} {request.supportedContact.lastName}
          </span>
        </>
      )}
      {isFundExpense && fundExpenseRecipientName && (
        <>
          {' - '}
          <span className="inline-flex items-center gap-1.5 align-middle">
            <img id="blade-recipient-info" src={infoIcon} alt="" className="w-4 h-4" />
            <CustomTooltip targetId="blade-recipient-info">
              <span className="body-xs-sm-regular">{fundExpenseDetails?.isAlreadyPaid ? t('payer') : t('supplier')}</span>
            </CustomTooltip>
            <span className="body-lr-regular">{fundExpenseRecipientName}</span>
          </span>
        </>
      )}
    </span>
  );

  const headerBottom = (
    <>
      <div className="flex gap-2">
        <span className={`${styles.amount} body-sm-medium ${amountColors}`}>
          <img src={donation} alt="" />
          {request.amount.toLocaleString(i18n.language, {
            style: 'currency',
            currency: 'ILS',
          })}
        </span>
        <span className={`${styles.status} body-sm-medium`}>
          <span className={styles['status-color']} style={{ '--status-color': getStatusColor(getAggregatedStatus(request.statuses)) } as CSSProperties}></span>
          {t(getAggregatedStatus(request.statuses))}
        </span>
      </div>
      {/*
       * HAT-1170 — מספר הבקשה, לא היה בכותרת המגירה בכלל. הכרטיס
       * במסך צר הציג אותו כשדה מטא קבוע; כשהשורה התקצרה כדי שהכרטיס
       * לא ייראה עמוס, הוא עבר לכאן — הוא כבר לא מוצג בשום מקום אחר
       * במסך צר, וזה בדיוק המספר שרכזת מקריאה למשרד בטלפון.
       */}
      <span className="body-sm-regular" dir="ltr">
        {getShortRequestId(request.correlationId)} · {formatDateLocale(request.requestedAt, i18n.language)}
      </span>
    </>
  );

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="520px">
      <Tabs defaultValue="statusDocuments" style={{ direction: 'inherit' }}>
        <TabsList>
          <TabsTrigger value="statusDocuments">{t('statusAndDocuments')}</TabsTrigger>
          <TabsTrigger value="additionalInfo">{t('additionalInfo')}</TabsTrigger>
        </TabsList>
        <TabsContent value="statusDocuments" className={`${styles.content} custom-scrollbar`}>
          {/* HAT-1395: renders nothing unless the request pays to an account deleted for a returned payment. */}
          <ReturnedPaymentSection correlationId={request.correlationId} onUpdateBankAccount={onUpdateBankAccount} />

          {/* Request Status Section */}
          <div className="flex flex-col gap-3">
            <h3 className="body-md-semibold text-(--font-heading)">
              {request.type === SupportRequestType.Monthly ? t('monthlyRequestDetails') : t('requestDetails')}
            </h3>
            {/* Monthly Request Details Table */}
            {request.type === SupportRequestType.Monthly && (
              <table className={`w-full ${styles.bladeTable}`}>
                <thead className={styles.thead}>
                  <tr>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('counter')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('date')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('requestedAmount')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('approvedAmount')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading
                    ? // Skeleton rows while loading
                      [1, 2, 3].map((row) => (
                        <tr key={row}>
                          {[1, 2, 3, 4, 5].map((col) => (
                            <td key={col} className={styles.td}>
                              <Skeleton variant="text" animation="pulse" sx={{ bgcolor: 'var(--surface-grey-lighter)' }} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : additionalInfo?.supportRequests?.map((supportRequest) => (
                        <tr key={supportRequest.multiRequestCounter}>
                          <td className={`body-sm-regular ${styles.td}`}>{supportRequest.multiRequestCounter}</td>
                          <td className={`body-sm-regular ${styles.td}`}>{formatDateLocale(supportRequest.requestedForDate, i18n.language)}</td>
                          <td className={`body-sm-regular ${styles.td}`}>
                            {supportRequest.requestedAmount.toLocaleString(i18n.language, {
                              style: 'currency',
                              currency: 'ILS',
                            })}
                          </td>
                          <td className={`body-sm-regular ${styles.td}`}>
                            {supportRequest.approvedAmount.toLocaleString(i18n.language, {
                              style: 'currency',
                              currency: 'ILS',
                            })}
                          </td>
                          <td className={`body-sm-regular ${styles.td}`}>{t(`requestDetailStatus.${getDetailStatusDisplayId(supportRequest.status)}`)}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            )}
            {/* One-time Request - show only the support transfer date */}
            {request.type === SupportRequestType.OneTime &&
              (isLoading ? (
                <TextRowsSkeleton rows={[{ width: '200px', height: '20px' }]} />
              ) : (
                <>
                  <p className="body-sm-regular">
                    <strong>{t('date')}:</strong> {formatDateLocale(additionalInfo?.supportRequests?.[0]?.requestedForDate || '', i18n.language)}
                  </p>
                  <p className="body-sm-regular">
                    <strong>{t('requestedAmount')}:</strong>{' '}
                    {additionalInfo?.supportRequests?.[0]?.requestedAmount.toLocaleString(i18n.language, {
                      style: 'currency',
                      currency: 'ILS',
                    })}
                  </p>
                  <p className="body-sm-regular">
                    <strong>{t('approvedAmount')}:</strong>{' '}
                    {additionalInfo?.supportRequests?.[0]?.approvedAmount.toLocaleString(i18n.language, {
                      style: 'currency',
                      currency: 'ILS',
                    })}
                  </p>
                </>
              ))}
          </div>
          {/* TODO: Timeline code commented out temporarily */}
          {/* <div className="flex flex-col gap-4">
            <h3 className="body-md-semibold text-(--font-heading)">{t('statusByTimeLine')}</h3>
            <div>
              <div className={styles['status-container']}>
                <img className={`bg-(--surface-tertiary-low) ${styles['status-icon']}`} src={requestSupport} alt="" />
                <div>
                  <p className="body-md-bold text-(--font-body-1)">{t('submitRequest')}</p>
                  <p className="body-sm-regular text-(--font-body-2)">
                    {Intl.DateTimeFormat('he-IL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    }).format(new Date(request.requestedAt))}
                  </p>
                </div>
              </div>
              <img src={timeline} alt="" className={styles.timeline} />
              <div className={styles['status-container']}>
                <img
                  className={`${numberStatus >= SupportRequestStatuses.PendingInitialApproval ? 'bg-(--surface-tertiary-low)' : 'bg-(--surface-grey-low)'} ${styles['status-icon']}`}
                  src={numberStatus >= SupportRequestStatuses.PendingInitialApproval ? waitingApproval : waitingApprovalInactive}
                  alt=""
                />
                <div>
                  <p
                    className={`body-md-bold ${numberStatus >= SupportRequestStatuses.PendingInitialApproval ? 'text-(--font-body-1)' : 'text-(--font-body-3)'}`}
                  >
                    {t('waitingApproval')}
                  </p>
                  {numberStatus >= SupportRequestStatuses.PendingInitialApproval && numberStatus <= SupportRequestStatuses.PendingExceptionsCommitteeApproval && (
                    <p className="body-sm-regular text-(--font-body-2)">
                      {Intl.DateTimeFormat('he-IL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      }).format(new Date(lastChangedDate))}
                    </p>
                  )}
                </div>
              </div>
              <img src={numberStatus >= SupportRequestStatuses.PendingInitialApproval ? timeline : timelineInactive} alt="" className={styles.timeline} />
              <div className={styles['status-container']}>
                <img
                  className={`${numberStatus >= SupportRequestStatuses.PendingExecution ? 'bg-(--surface-tertiary-low)' : 'bg-(--surface-grey-low)'} ${styles['status-icon']}`}
                  src={numberStatus >= SupportRequestStatuses.PendingExecution ? approvedSupport : approvedSupportInactive}
                  alt=""
                />
                <div>
                  <p className={`body-md-bold ${numberStatus >= SupportRequestStatuses.PendingExecution ? 'text-(--font-body-1)' : 'text-(--font-body-3)'}`}>
                    {t('requestApproval')}
                  </p>
                  {numberStatus == SupportRequestStatuses.PendingExecution && (
                    <p className="body-sm-regular text-(--font-body-2)">
                      {Intl.DateTimeFormat('he-IL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      }).format(new Date(lastChangedDate))}
                    </p>
                  )}
                </div>
              </div>
              <img src={numberStatus >= SupportRequestStatuses.PendingExecution ? timeline : timelineInactive} alt="" className={styles.timeline} />
              <div className={styles['status-container']}>
                <img
                  className={`${numberStatus == SupportRequestStatuses.Done ? 'bg-(--surface-tertiary-low)' : 'bg-(--surface-grey-low)'} ${styles['status-icon']}`}
                  src={numberStatus == SupportRequestStatuses.Done ? requestExecution : requestExecutionInactive}
                  alt=""
                />
                <div>
                  <p className={`body-md-bold ${numberStatus == SupportRequestStatuses.Done ? 'text-(--font-body-1)' : 'text-(--font-body-3)'}`}>
                    {t('requestExecution')}
                  </p>
                  {numberStatus == SupportRequestStatuses.Done && (
                    <p className="body-sm-regular text-(--font-body-2)">
                      {Intl.DateTimeFormat('he-IL', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      }).format(new Date(lastChangedDate))}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div> */}
          <div className="flex flex-col gap-3">
            <h3 className="body-md-semibold text-(--font-heading)">{t('documentsAndApprovals')}</h3>
            <table className={`w-full ${styles.bladeTable}`}>
              <thead className={styles.thead}>
                <tr>
                  <th className={`${styles.th} body-xs-sm-bold`}>
                    {t('document')}/{t('approval')}
                  </th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('uploadDate')}</th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('status')}</th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('officeComment')}</th>
                  <th className={`${styles.th} body-xs-sm-bold`}>{t('userComment')}</th>
                </tr>
              </thead>
              <tbody>
                {documents.length > 0 ? (
                  documents.map((document) => (
                    <tr key={document.id}>
                      <td className={`body-sm-regular ${styles.td}`}>
                        <div className="flex items-center gap-1">
                          <a target="_blank" rel="noopener noreferrer" href={document.uri}>
                            {document.name}
                          </a>
                          <DownloadFileButton uri={document.uri} fileName={document.name} />
                        </div>
                      </td>
                      <td className={`body-sm-regular ${styles.td}`}>{formatDateLocale(String(document.creationTime), i18n.language)}</td>
                      <td className={`body-sm-regular ${styles.td}`}>
                        {document.status === DocumentStatus.Rejected ? (
                          <span className="text-(--font-error)">{t('documentStatus.Rejected')}</span>
                        ) : (
                          <span className="text-(--font-body-3)">-</span>
                        )}
                      </td>
                      <td className={`body-sm-regular ${styles.td}`}>{document.officeComment || '-'}</td>
                      <td className={`body-sm-regular ${styles.td}`}>{document.userComment || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.noDocuments}>
                        <img src={documentsIcon} alt="" />
                        <p className="body-md-regular text-(--font-body-3)">{t('noDocumentsOrApprovalsAttachedToRequest')}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {canUploadDocuments && (
              <CompletionUploadSection
                apiEndpoint={`funds/${currentFund?.id}/support-requests/${request.correlationId}/completion-documents`}
                onSuccess={handleCompletionSuccess}
                showAlert={isPendingCompletion}
              />
            )}
          </div>

          {/* Office Comments Section - only shown if there are visible comments */}
          {additionalInfo?.visibleOfficeComments && additionalInfo.visibleOfficeComments.length > 0 && (
            <div className="flex flex-col gap-3">
              <h3 className="body-md-semibold text-(--font-heading)">{t('officeComments.title')}</h3>
              <table className={styles.officeCommentsTable}>
                <thead className={styles.thead}>
                  <tr>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('officeComments.comment')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('officeComments.date')}</th>
                    <th className={`${styles.th} body-xs-sm-bold`}>{t('officeComments.sentOn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalInfo.visibleOfficeComments.map((comment, index) => (
                    <tr key={index}>
                      <td className={`body-sm-regular ${styles.td} ${styles.commentCell}`}>{comment.comment}</td>
                      <td className={`body-sm-regular ${styles.td} ${styles.dateCell}`}>{formatDateLocale(comment.date, i18n.language)}</td>
                      <td className={`body-sm-regular ${styles.td} ${styles.dateCell}`}>
                        {comment.sendDate ? formatDateLocale(comment.sendDate, i18n.language) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="additionalInfo" className={`${styles.content} custom-scrollbar`}>
          {sections.map((section, index, arr) => (
            <>
              {section}
              {index !== arr.length - 1 && <div className={styles.line} />}
            </>
          ))}
        </TabsContent>
      </Tabs>
    </Blade>
  );
};

export default AdditionalInfo;
